import { responsiblePlhIdOfClient } from "./client-plh-lookup.js";
import type { RequestContext } from "../context/request-context.js";
import type { LpReportRepository } from "../../repositories/operational-repositories.js";
import {
  type LpDocumentModel,
  type LpHistoryEntry,
  type LpReport,
  type LpSignatory,
  type LpValidationResult,
  LP_RESPONSIBILITY_TERM,
  LP_SIGNATORY_ROLE_LABELS,
  emptyConsumableRef,
  formatIllumination,
  formatLpLaudo,
  formatPenetrantFamily,
  isEmbeddableImage,
  normalizeLpReport,
  validateLpReportCompleteness
} from "../../domain/ndt/lp-report-domain.js";
import { LP_CONSUMABLE_TYPE_LABELS, formatCivilDateBR, isLpConsumableExpired } from "../../domain/ndt/lp-consumable.js";
import { canActAsPlh } from "../../domain/identity/plh-eligibility.js";
import { extractCatalogCode } from "../../domain/ndt/ndt-technical-catalog.js";
import type { ClientId, EquipmentId } from "../../shared/kernel/identifiers.js";
import type { DocumentNumberAllocator } from "./document-numbering-service.js";
import type { DocumentVisual, TemplatedDocument } from "../../domain/studio/visual-design.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import type { DocumentSnapshot } from "../../infrastructure/documents/document-engine.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../permissions/permissions.js";
import type { DocumentReviewHooks } from "./technical-review-service.js";
import type { DocumentHoldHooks } from "./document-hold-service.js";

const randomUUID = () => globalThis.crypto.randomUUID();

/** Os Profissionais por papel mudaram entre o gravado e o enviado? */
export function signatoriesDiffer(existing: LpReport, incoming: LpReport): boolean {
  const key = (r: LpReport) =>
    (["INSPETOR", "PLH", "FISCAL"] as const)
      .map(role => `${role}=${r.signatories?.find(s => s.role === role)?.professionalId ?? ""}`)
      .join("|") + `|inspector=${r.inspectorProfessionalId ?? ""}`;
  return key(existing) !== key(incoming);
}

/** Função mostrada para um signatário cujo Profissional não tem functionTitle cadastrado. */
const DEFAULT_SIGNATORY_TITLES: Record<LpSignatory["role"], string> = {
  INSPETOR: "Inspetor de Equipamentos",
  PLH: "Engenheiro – PLH",
  FISCAL: "Fiscal"
};

export interface LpReportSeed {
  clientId?: string | null;
  clientDisplayName?: string;
  projectId?: string | null;
  projectContract?: string;
  unitAreaId?: string | null;
  unitArea?: string;
  siteLocation?: string;
  equipmentId?: string | null;
  equipmentTag?: string;
  equipmentTypeLabel?: string;
  component?: string;
  baseMaterial?: string;
  thicknessOrDiameter?: string;
  inspectionId?: string | null;
  inspectionNumber?: string;
  inspectorProfessionalId?: string | null;
  inspectorName?: string;
}

/** Resolve uma Inspeção pelo id — só o necessário para conferir o vínculo, sem acoplar o LP ao repositório de inspeções. */
export type LpInspectionLookup = (inspectionId: string) => Promise<{ id: string; equipmentId: string; clientId: string } | null>;

export class LpReportService {
  /** Revisão PLH central: bloqueia edição enquanto o LP está com o PLH e condiciona a assinatura à aprovação. */
  private reviewHooks: DocumentReviewHooks | null = null;

  constructor(
    private readonly repo: LpReportRepository,
    private readonly identityRepo?: any,
    private readonly inspectionLookup?: LpInspectionLookup
  ) {}

  attachReviewHooks(hooks: DocumentReviewHooks): void {
    this.reviewHooks = hooks;
  }

  /** HOLD: enquanto o LP está EM HOLD nada é salvo/validado/assinado — o status do módulo não muda. */
  private holdHooks: DocumentHoldHooks | null = null;

  attachHoldHooks(hooks: DocumentHoldHooks): void {
    this.holdHooks = hooks;
  }

  private async assertNotOnHold(id: string): Promise<void> {
    if (!this.holdHooks) return;
    const hold = await this.holdHooks.state("LP", id);
    if (hold.onHold) throw new AppError(ErrorCode.Conflict, "LP report on hold", { safeMessage: hold.reason });
  }

  private async assertNotLockedByReview(id: string): Promise<void> {
    await this.assertNotOnHold(id);
    if (!this.reviewHooks) return;
    const state = await this.reviewHooks.state("LP", id);
    if (state.locked) throw new AppError(ErrorCode.Conflict, "LP report locked by PLH review", { safeMessage: state.reason });
  }

  /**
   * A Inspeção é OPCIONAL. Quando informada, precisa existir e pertencer ao
   * Equipamento e ao Cliente do relatório — um vínculo incompatível é
   * recusado com o motivo, não corrigido por conta própria.
   */
  private async assertInspectionMatchesContext(
    inspectionId: string | null | undefined,
    equipmentId: string | null | undefined,
    clientId: string
  ): Promise<void> {
    if (!inspectionId || !this.inspectionLookup) return;
    const inspection = await this.inspectionLookup(inspectionId);
    if (!inspection) {
      throw new AppError(ErrorCode.NotFound, `Inspection ${inspectionId} not found`, {
        safeMessage: "A Inspeção selecionada não foi encontrada."
      });
    }
    if (inspection.clientId && inspection.clientId !== clientId) {
      throw new AppError(ErrorCode.Validation, "Inspection belongs to another client", {
        safeMessage: "A Inspeção selecionada pertence a outro Cliente."
      });
    }
    if (equipmentId && inspection.equipmentId && inspection.equipmentId !== equipmentId) {
      throw new AppError(ErrorCode.Validation, "Inspection belongs to another equipment", {
        safeMessage: "A Inspeção selecionada pertence a outro Equipamento — escolha uma Inspeção deste Equipamento ou deixe o campo vazio."
      });
    }
  }

  async getReport(context: RequestContext, id: string): Promise<LpReport | null> {
    new RbacAuthorizationService().require(context, PermissionCode.LpReportView);
    const report = await this.repo.findById(context, id);
    return report ? normalizeLpReport(report) : null;
  }

  async listByClient(context: RequestContext, clientId?: string): Promise<readonly LpReport[]> {
    new RbacAuthorizationService().require(context, PermissionCode.LpReportView);
    const target = clientId ?? context.activeClientId;
    if (!target) return [];
    return this.repo.listByClient(context, target);
  }

  /** O LP já preso a uma Inspeção — a página o abre em vez de criar um segundo. */
  async getByInspection(context: RequestContext, inspectionId: string): Promise<LpReport | null> {
    new RbacAuthorizationService().require(context, PermissionCode.LpReportView);
    const report = await this.repo.findByInspection(context, inspectionId);
    return report ? normalizeLpReport(report) : null;
  }

  /**
   * Cria um rascunho com id e número gerados uma única vez — nunca
   * regenerados nos salvamentos seguintes. O Cliente vem do contexto escolhido
   * na própria página; o cliente global da sessão é só um recurso de última
   * instância.
   */
  async createDraft(context: RequestContext, seed?: LpReportSeed): Promise<LpReport> {
    new RbacAuthorizationService().require(context, PermissionCode.LpReportCreate);

    const clientId = (seed?.clientId || context.activeClientId) as ClientId;
    if (!clientId) {
      throw new AppError(ErrorCode.Validation, "No client selected for the LP report", {
        safeMessage: "Selecione o Cliente do relatório antes de criar o Relatório LP."
      });
    }

    if (seed?.inspectionId) {
      await this.assertInspectionMatchesContext(seed.inspectionId, seed.equipmentId ?? null, clientId);
      const existing = await this.repo.findByInspection(context, seed.inspectionId);
      if (existing) {
        throw new AppError(ErrorCode.Conflict, "LP report already exists for this inspection", {
          safeMessage: `Já existe um Relatório LP para esta Inspeção (Nº ${existing.reportNumber}). Abra o relatório existente em vez de criar outro.`
        });
      }
    }

    const now = new Date().toISOString();
    const reportNumber = await this.generateReportNumber(context, clientId, { projectId: seed?.projectId ?? null, unitAreaId: seed?.unitAreaId ?? null });
    const by = context.userId || "system";

    const report: LpReport = {
      id: `lp-${randomUUID()}`,
      clientId,
      reportNumber,
      clientReportNumber: "",
      revision: "00",
      status: "DRAFT",

      projectId: seed?.projectId ?? null,
      unitAreaId: seed?.unitAreaId ?? null,
      equipmentId: (seed?.equipmentId as EquipmentId) || null,
      inspectionId: (seed?.inspectionId as any) || null,

      clientDisplayName: seed?.clientDisplayName || "",
      projectContract: seed?.projectContract || "",
      siteLocation: seed?.siteLocation || "",
      unitArea: seed?.unitArea || "",
      equipmentTag: seed?.equipmentTag || "",
      equipmentTypeLabel: seed?.equipmentTypeLabel || "",
      component: seed?.component || "",
      inspectionNumber: seed?.inspectionNumber || "",
      osWorkOrderId: null,
      osNumber: "",
      osSubWorkOrderId: null,
      subOsNumber: "",
      testDate: "",
      inspectorProfessionalId: (seed?.inspectorProfessionalId as any) ?? context.professionalId ?? null,
      inspectorName: seed?.inspectorName || "",

      procedureId: null,
      procedureRevision: "",
      referenceNormId: null,
      referenceNorm: "",
      acceptanceCriteriaId: null,
      acceptanceCriteria: "",
      testTemperature: "",

      baseMaterial: seed?.baseMaterial || "",
      thicknessOrDiameter: seed?.thicknessOrDiameter || "",
      penetrantFamily: "",
      illuminationType: "",
      illuminationIntensity: "",
      penetrant: emptyConsumableRef(),
      remover: emptyConsumableRef(),
      developer: emptyConsumableRef(),
      penetrationTime: "",

      results: [],
      croqui: null,
      photos: [],
      observations: "",
      // Com Inspeção vinculada, o Inspetor dela já entra como signatário —
      // preenchimento do sistema a partir do Profissional real, não uma
      // escolha do usuário (essa fica com a Engenharia).
      signatories: await this.prefillInspectorSignatory(seed?.inspectorProfessionalId ?? null),
      issueDate: now,
      history: [{ at: now, by, action: "CREATED", detail: `Rascunho ${reportNumber} criado.` }],
      frozenSnapshot: null,

      signedAt: null,
      signedByUserId: null,
      signedByProfessionalId: null,

      createdAt: now,
      updatedAt: now,
      createdBy: by
    };

    await this.repo.save(context, report);
    return report;
  }

  /**
   * "Salvar Rascunho": grava o relatório inteiro preservando id, número,
   * revisão, criação e histórico — um relatório assinado não muda mais.
   */
  async saveDraft(context: RequestContext, report: LpReport): Promise<LpReport> {
    new RbacAuthorizationService().require(context, PermissionCode.LpReportEdit);

    const existing = await this.repo.findById(context, report.id);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, "LP report not found", { safeMessage: "Relatório LP não encontrado." });
    }
    if (existing.status === "SIGNED") {
      throw new AppError(ErrorCode.Conflict, "Signed LP report cannot be edited", {
        safeMessage: "Relatório assinado não pode mais ser editado."
      });
    }
    await this.assertNotLockedByReview(report.id);

    // Trocar quem assina é decisão de Engenharia: sem a permissão, o que
    // vier diferente do gravado é recusado — os cards são só leitura.
    if (signatoriesDiffer(existing, report)) {
      if (!context.permissions.has(PermissionCode.LpReportManageSignatories)) {
        throw new AppError(ErrorCode.Authorization, "Changing LP signatories requires engineering permission", {
          safeMessage: "Somente o Engenheiro autorizado pode alterar os signatários do Relatório LP."
        });
      }
    }

    // Vincular uma Inspeção na edição segue a mesma regra da criação.
    if (report.inspectionId && report.inspectionId !== existing.inspectionId) {
      await this.assertInspectionMatchesContext(report.inspectionId, report.equipmentId ?? existing.equipmentId, existing.clientId);
    }

    const now = new Date().toISOString();
    const updated: LpReport = {
      ...normalizeLpReport({ ...report }),
      id: existing.id,
      clientId: existing.clientId,
      reportNumber: existing.reportNumber,
      revision: existing.revision,
      createdAt: existing.createdAt,
      createdBy: existing.createdBy,
      // A trilha é do registro: a página não a reescreve.
      history: this.appendHistory(existing.history, { at: now, by: context.userId || "system", action: "SAVED", detail: "Rascunho salvo." }),
      frozenSnapshot: existing.frozenSnapshot,
      status: existing.status === "VALIDATED" ? "DRAFT" : existing.status,
      issueDate: now,
      updatedAt: now
    };

    await this.repo.save(context, updated);
    if (this.reviewHooks) await this.reviewHooks.correctionSaved(context, "LP", updated.id);
    return updated;
  }

  /** "Validar Dados": conferência de completude; marca VALIDATED só quando tudo está presente. */
  async validate(context: RequestContext, id: string): Promise<LpValidationResult> {
    await this.assertNotOnHold(id);
    new RbacAuthorizationService().require(context, PermissionCode.LpReportValidate);

    const report = await this.repo.findById(context, id);
    if (!report) {
      throw new AppError(ErrorCode.NotFound, "LP report not found", { safeMessage: "Relatório LP não encontrado." });
    }
    normalizeLpReport(report);
    const result = validateLpReportCompleteness(report);

    if (report.status !== "SIGNED") {
      const now = new Date().toISOString();
      await this.repo.save(context, {
        ...report,
        status: result.valid ? "VALIDATED" : "DRAFT",
        history: this.appendHistory(report.history, {
          at: now,
          by: context.userId || "system",
          action: "VALIDATED",
          detail: result.valid ? "Dados validados — relatório completo." : `Validação apontou ${result.errors.length} pendência(s).`
        }),
        updatedAt: now
      });
    }

    return result;
  }

  /**
   * O modelo documental — Preview e PDF consomem exatamente este objeto.
   * Depois de assinado devolve o snapshot congelado, não os cadastros vivos.
   */
  async buildDocumentModel(context: RequestContext, id: string): Promise<LpDocumentModel> {
    new RbacAuthorizationService().require(context, PermissionCode.LpReportView);

    const report = await this.repo.findById(context, id);
    if (!report) {
      throw new AppError(ErrorCode.NotFound, "LP report not found", { safeMessage: "Relatório LP não encontrado." });
    }
    normalizeLpReport(report);

    if (report.status === "SIGNED" && report.frozenSnapshot) return report.frozenSnapshot;
    return this.composeModel(report);
  }

  /** Snapshot para o motor de documentos — "Visualizar" e "Gerar PDF" passam por aqui. */
  async buildDocumentSnapshot(context: RequestContext, id: string): Promise<DocumentSnapshot> {
    const model = await this.buildDocumentModel(context, id);
    const report = (await this.repo.findById(context, id))!;
    return {
      id: report.reportNumber,
      type: "LP",
      revision: report.revision,
      documentNumber: report.reportNumber,
      clientId: report.clientId,
      createdAt: new Date(report.issueDate),
      data: model
    };
  }

  /** Registra na trilha que um PDF foi gerado — sem tocar no conteúdo. */
  async recordPdfGenerated(context: RequestContext, id: string): Promise<void> {
    const report = await this.repo.findById(context, id);
    if (!report) return;
    const now = new Date().toISOString();
    await this.repo.save(context, {
      ...report,
      history: this.appendHistory(report.history, { at: now, by: context.userId || "system", action: "PDF_GENERATED", detail: `PDF gerado (Rev. ${report.revision}).` })
    });
  }

  /**
   * Assinatura como PLH: exige LpReportApprove + LpReportSign E um Profissional
   * realmente habilitado (canActAsPlh). ADMIN/DEVELOPER não satisfazem isso
   * por si. Ao assinar, o documento é CONGELADO: cadastros, consumíveis,
   * imagens e assinaturas ficam como estavam neste instante.
   */
  async signAsPlh(context: RequestContext, id: string): Promise<LpReport> {
    await this.assertNotOnHold(id);
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.LpReportApprove);
    auth.require(context, PermissionCode.LpReportSign);

    const report = await this.repo.findById(context, id);
    if (!report) {
      throw new AppError(ErrorCode.NotFound, "LP report not found", { safeMessage: "Relatório LP não encontrado." });
    }
    normalizeLpReport(report);
    if (report.status === "SIGNED") {
      throw new AppError(ErrorCode.Conflict, "LP report already signed", { safeMessage: "Relatório já assinado." });
    }
    // Com ciclo de revisão aberto, assinar só depois de "Aprovado pelo PLH" (revisar → aprovar → assinar).
    if (this.reviewHooks) {
      const state = await this.reviewHooks.state("LP", id);
      if (state.review && !state.approved) {
        throw new AppError(ErrorCode.Conflict, "LP review not approved", { safeMessage: "O relatório só pode ser assinado depois de aprovado pelo PLH na revisão." });
      }
    }

    const completeness = validateLpReportCompleteness(report);
    if (!completeness.valid) {
      throw new AppError(ErrorCode.Validation, "LP report incomplete", {
        safeMessage: `O relatório não pode ser assinado com pendências: ${completeness.errors.join(" ")}`
      });
    }

    const user = this.identityRepo && context.userId ? await this.identityRepo.getUser(context.userId) : null;
    const professional = this.identityRepo && user?.professionalId ? await this.identityRepo.getProfessional(user.professionalId) : null;
    // Habilitação pelo CLIENTE DO DOCUMENTO: quem o Cliente nomeou responsável.
    const eligibility = canActAsPlh(user, professional, await responsiblePlhIdOfClient(this.identityRepo as never, String(report.clientId)));
    if (!eligibility.allowed) {
      throw new AppError(ErrorCode.Authorization, eligibility.reason || "Usuário não habilitado como PLH.", {
        safeMessage: eligibility.reason || "Você não está habilitado a assinar como PLH."
      });
    }

    // O Engenheiro escolheu quem é o PLH do documento; quem assina como PLH
    // precisa ser exatamente esse Profissional. Escolher não torna ninguém PLH.
    const chosenPlh = report.signatories.find(s => s.role === "PLH" && s.professionalId);
    if (chosenPlh && chosenPlh.professionalId !== professional.id) {
      throw new AppError(ErrorCode.Authorization, "Signer is not the selected PLH", {
        safeMessage: `O PLH selecionado para este relatório é ${chosenPlh.name || chosenPlh.professionalId}. Somente ele pode assinar como PLH.`
      });
    }

    const now = new Date().toISOString();

    // O PLH que assina é o signatário PLH do documento — sempre o Profissional
    // real do usuário, nunca texto livre.
    const signatories = report.signatories.filter(s => s.role !== "PLH");
    signatories.push({
      role: "PLH",
      professionalId: professional.id,
      name: professional.name || "",
      functionTitle: professional.functionTitle || DEFAULT_SIGNATORY_TITLES.PLH,
      credential: this.credentialFor(professional),
      signatureImageId: professional.signatureImageId || null,
      signedAt: now
    });

    const signed: LpReport = {
      ...report,
      signatories,
      status: "SIGNED",
      signedAt: now,
      signedByUserId: context.userId || null,
      signedByProfessionalId: professional.id,
      issueDate: now,
      updatedAt: now
    };

    // Congela o documento como está agora — inclusive as imagens das
    // assinaturas resolvidas neste momento.
    signed.frozenSnapshot = await this.composeModel(signed);
    signed.history = this.appendHistory(report.history, {
      at: now,
      by: context.userId || "system",
      action: "SIGNED",
      detail: `Assinado como PLH por ${professional.name}. Documento congelado.`
    });

    await this.repo.save(context, signed);
    if (this.reviewHooks) await this.reviewHooks.signed(context, "LP", signed.id);
    return signed;
  }

  // ---------------------------------------------------------------------

  /**
   * STUDIO INSPECT IA: o visual PUBLICADO entra no modelo documental quando
   * ele é montado. Aprovado/congelado = modelo congelado = visual da época.
   * Sem a ligação (testes, composições antigas), o modelo sai sem visual e o
   * builder imprime o padrão vigente — exatamente como antes.
   */
  private documentVisual: ((type: TemplatedDocument) => Promise<DocumentVisual>) | null = null;

  attachDocumentVisual(provider: (type: TemplatedDocument) => Promise<DocumentVisual>): void {
    this.documentVisual = provider;
  }

  private async composeModel(report: LpReport): Promise<LpDocumentModel> {
    const consumables = (["penetrant", "remover", "developer"] as const).map(field => {
      const ref = report[field];
      const type = field === "penetrant" ? "PENETRANT" : field === "remover" ? "REMOVER" : "DEVELOPER";
      return {
        type: type as any,
        typeLabel: LP_CONSUMABLE_TYPE_LABELS[type as keyof typeof LP_CONSUMABLE_TYPE_LABELS],
        brand: ref.brand,
        product: ref.product,
        lot: ref.lot,
        validity: formatCivilDateBR(ref.validity),
        expired: isLpConsumableExpired(ref.validity)
      };
    });

    const photos = report.photos
      .filter(p => isEmbeddableImage(p.image))
      .map((p, index) => ({
        number: `Foto ${String(index + 1).padStart(2, "0")}`,
        image: p.image,
        item: p.item,
        location: p.location,
        comment: p.comment
      }));

    return {
      visual: this.documentVisual ? await this.documentVisual("LP").catch(() => null) : null,
      reportNumber: report.reportNumber,
      clientReportNumber: report.clientReportNumber,
      revision: report.revision,
      status: report.status,
      issueDate: formatCivilDateBR(report.issueDate.slice(0, 10)),
      osNumber: report.osNumber,
      subOsNumber: report.subOsNumber,
      qrPayload: `INSPECT-IA|LP|${report.reportNumber}|REV ${report.revision}|${report.id}`,

      identification: {
        client: report.clientDisplayName,
        projectContract: report.projectContract,
        unitArea: report.unitArea,
        siteLocation: report.siteLocation,
        equipmentTag: report.equipmentTag,
        equipmentDescription: report.equipmentTypeLabel,
        component: report.component,
        // Sem Inspeção vinculada fica vazio — o documento imprime "—".
        inspectionNumber: report.inspectionId ? report.inspectionNumber : "",
        testDate: formatCivilDateBR(report.testDate),
        osNumber: report.osNumber,
        subOsNumber: report.subOsNumber
      },

      testData: {
        procedure: report.procedureRevision,
        referenceNorm: extractCatalogCode(report.referenceNorm),
        acceptanceCriteria: report.acceptanceCriteria,
        baseMaterial: report.baseMaterial,
        thicknessOrDiameter: report.thicknessOrDiameter ? `${report.thicknessOrDiameter} mm` : "",
        penetrantFamily: formatPenetrantFamily(report.penetrantFamily),
        illuminationType: formatIllumination(report.illuminationType),
        illuminationIntensity: report.illuminationIntensity ? `${report.illuminationIntensity} Lux` : "",
        testTemperature: report.testTemperature ? `${report.testTemperature} °C` : "",
        penetrationTime: report.penetrationTime ? `${report.penetrationTime} min` : "",
        inspector: report.inspectorName
      },

      consumables,

      results: report.results.map(r => ({
        item: r.item,
        element: r.element,
        location: r.location,
        extension: r.extension,
        description: r.description,
        laudo: r.laudo,
        laudoLabel: formatLpLaudo(r.laudo)
      })),

      croqui: report.croqui && isEmbeddableImage(report.croqui.image)
        ? { image: report.croqui.image, fileName: report.croqui.fileName }
        : null,

      photos,

      observations: report.observations,

      responsibilityTerm: LP_RESPONSIBILITY_TERM,

      signatories: await this.resolveSignatories(report)
    };
  }

  /**
   * Signatários como o documento precisa: a imagem REAL da assinatura
   * cadastrada é resolvida aqui (mesma fonte do RI e do ME). Sem assinatura
   * cadastrada, o signatário mantém a identificação e um espaço em branco.
   * As três posições existem sempre — o documento carrega o campo formalmente.
   */
  private async resolveSignatories(report: LpReport): Promise<LpDocumentModel["signatories"]> {
    const roles: LpSignatory["role"][] = ["INSPETOR", "PLH", "FISCAL"];
    const byRole = new Map(report.signatories.map(s => [s.role, s]));

    return Promise.all(roles.map(async role => {
      const s = byRole.get(role);
      let signatureImage: string | null = null;
      if (s?.professionalId && this.identityRepo?.getProfessionalSignature) {
        try {
          signatureImage = await this.identityRepo.getProfessionalSignature(s.professionalId);
        } catch {
          signatureImage = null;
        }
      }
      const date = s?.signedAt || (s?.professionalId ? report.issueDate : "");
      return {
        role,
        roleLabel: LP_SIGNATORY_ROLE_LABELS[role],
        name: s?.name || "",
        functionTitle: s?.functionTitle || (s ? DEFAULT_SIGNATORY_TITLES[role] : ""),
        credential: s?.credential || "",
        signatureImage: isEmbeddableImage(signatureImage) ? signatureImage : null,
        date: date ? formatCivilDateBR(date.slice(0, 10)) : ""
      };
    }));
  }

  private credentialFor(p: any): string {
    return p.crea || p.cft || p.snqc || p.sncc || p.endIso9712 || p.endIso || "";
  }

  /** Signatário INSPETOR a partir do Profissional da Inspeção — vazio quando não há Inspeção ou cadastro. */
  private async prefillInspectorSignatory(professionalId: string | null): Promise<LpSignatory[]> {
    if (!professionalId || !this.identityRepo?.getProfessional) return [];
    let professional: any = null;
    try {
      professional = await this.identityRepo.getProfessional(professionalId);
    } catch {
      professional = null;
    }
    if (!professional) return [];
    return [{
      role: "INSPETOR",
      professionalId: professional.id,
      name: professional.name || "",
      functionTitle: professional.functionTitle || DEFAULT_SIGNATORY_TITLES.INSPETOR,
      credential: this.credentialFor(professional),
      signatureImageId: professional.signatureImageId || null,
      signedAt: null
    }];
  }

  private appendHistory(history: LpHistoryEntry[] | undefined, entry: LpHistoryEntry): LpHistoryEntry[] {
    return [...(Array.isArray(history) ? history : []), entry];
  }

  /**
   * CONFIGURAÇÕES → NUMERAÇÃO. Com o alocador ligado, o número vem do modelo
   * configurado e de uma sequência ATÔMICA; sem ele (testes, composições que
   * não o fornecem), vale o gerador histórico.
   */
  private numbering: DocumentNumberAllocator | null = null;

  attachNumberAllocator(allocator: DocumentNumberAllocator): void {
    this.numbering = allocator;
  }

  private async generateReportNumber(context: RequestContext, clientId: string, refs: { projectId?: string | null; unitAreaId?: string | null } = {}): Promise<string> {
    const all = await this.repo.listByClient(context, clientId);
    const year = new Date().getFullYear();
    const prefix = `LP-${year}-`;
    const count = all.filter(r => r.reportNumber.startsWith(prefix)).length;
    if (this.numbering) {
      return this.numbering.allocate({
        documentType: "LP",
        clientId, projectId: refs.projectId ?? null, unitAreaId: refs.unitAreaId ?? null,
        legacyCount: async () => count,
        isTaken: async (candidate, scope) => {
          const pool = scope.perClient || !this.repo.listAll ? await this.repo.listByClient(context, clientId) : await this.repo.listAll();
          return pool.some(r => r.reportNumber === candidate);
        }
      });
    }
    return `${prefix}${String(count + 1).padStart(4, "0")}`;
  }
}
